import { createApp } from "vue";
import {
  createRouter,
  createWebHistory,
  RouterLink,
  RouterView,
} from "vue-router";
import { createPinia, defineStore } from "pinia";
import axios from "axios";
import "./style.css";
import App from "./App.vue";

/**
 * Axios client
 * - Uses VITE_API_BASE at runtime (production) or '/api' (proxied in dev)
 */
const apiBase =
  (import.meta && import.meta.env && import.meta.env.VITE_API_BASE) || "/api";

export const api = axios.create({
  baseURL: apiBase,
  timeout: 15000,
});

/**
 * Auth Store (Pinia)
 */
export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: localStorage.getItem("jwt") || "",
    adminSecret: localStorage.getItem("admin_secret") || "",
    member: (() => {
      try {
        return JSON.parse(localStorage.getItem("member") || "null");
      } catch {
        return null;
      }
    })(),
  }),
  getters: {
    isAuthenticated: (s) => !!s.token,
    role: (s) => (s.member && s.member.role) || "member",
    isAdmin: (s) => !!s.adminSecret,
    organizationId: (s) => (s.member && s.member.organization_id) || null,
    email: (s) => (s.member && s.member.email) || "",
  },
  actions: {
    setAuth(token, member) {
      this.token = token || "";
      this.member = member || null;
      if (this.token) localStorage.setItem("jwt", this.token);
      else localStorage.removeItem("jwt");
      if (this.member)
        localStorage.setItem("member", JSON.stringify(this.member));
      else localStorage.removeItem("member");
    },
    clearAuth() {
      this.setAuth("", null);
    },
    setAdminSecret(secret) {
      this.adminSecret = secret || "";
      if (this.adminSecret)
        localStorage.setItem("admin_secret", this.adminSecret);
      else localStorage.removeItem("admin_secret");
    },
    async signIn(email, password) {
      const resp = await api.post("/auth/signin", { email, password });
      const { token, member } = resp.data || {};
      this.setAuth(token, member);
      return member;
    },
    async signOut() {
      this.clearAuth();
    },
  },
});

/**
 * Sync tabs
 */
window.addEventListener("storage", (e) => {
  if (e.key === "jwt" || e.key === "member") {
    try {
      const auth = useAuthStore();
      const token = localStorage.getItem("jwt") || "";
      const member = JSON.parse(localStorage.getItem("member") || "null");
      auth.setAuth(token, member);
    } catch {
      /* noop */
    }
  }
});

/**
 * Axios interceptors
 */
let router; // set later

api.interceptors.request.use(
  (config) => {
    try {
      const auth = useAuthStore();
      config.headers = config.headers || {};
      if (auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`;
      }
      if (auth.adminSecret) {
        config.headers["X-Admin-Secret"] = auth.adminSecret;
      }
    } catch {
      /* noop */
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      try {
        const auth = useAuthStore();
        auth.clearAuth();
      } catch {
        /* noop */
      }
      if (router) {
        const current = router.currentRoute.value;
        if (current?.path !== "/login") {
          router.replace({
            path: "/login",
            query: { redirect: current?.fullPath || "/" },
          });
        }
      }
    }
    return Promise.reject(error);
  },
);

/* -----------------------------------------------------------------------------
   UI Components (Swiss style: bold typography, whitespace, grid, brand accents)
 ----------------------------------------------------------------------------- */

const AppHeader = {
  name: "AppHeader",
  computed: {
    auth() {
      return useAuthStore();
    },
  },
  data() {
    return {
      adminSecret: "",
    };
  },
  methods: {
    setAdminSecret() {
      const auth = useAuthStore();
      auth.setAdminSecret(this.adminSecret);
    },
    async doSignOut() {
      const auth = useAuthStore();
      await auth.signOut();
      this.$router.replace("/login");
    },
  },
  template: `
    <header class="border-b border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-30">
      <div class="container flex items-center justify-between py-4">
        <div class="flex items-center gap-3">
          <div class="h-8 w-8 rounded-md bg-brand-500 flex items-center justify-center shadow-soft">
            <span class="material-symbols-rounded text-white text-[18px]">shield_person</span>
          </div>
          <RouterLink to="/dashboard" class="font-extrabold tracking-tight text-xl">Access System</RouterLink>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="auth.isAuthenticated" class="hidden sm:inline text-sm text-gray-500">{{ auth.email }} ({{ auth.role }})</span>
          <input v-model="adminSecret" class="input w-40 hidden md:block" placeholder="Admin secret" />
          <button class="btn hidden md:inline-flex" @click="setAdminSecret" title="Set admin secret">
            <span class="material-symbols-rounded">key</span>
            Set
          </button>
          <RouterLink v-if="auth.isAuthenticated" to="/me" class="btn hidden sm:inline-flex">
            <span class="material-symbols-rounded">person</span>
            Profile
          </RouterLink>
          <button v-if="auth.isAuthenticated" @click="doSignOut" class="btn" title="Sign out">
            <span class="material-symbols-rounded">logout</span>
            <span class="hidden sm:inline">Sign out</span>
          </button>
          <RouterLink v-else to="/login" class="btn-primary">
            <span class="material-symbols-rounded">login</span>
            Sign in
          </RouterLink>
        </div>
      </div>
    </header>
  `,
  components: { RouterLink },
};

const AppSidebar = {
  name: "AppSidebar",
  computed: {
    auth() {
      return useAuthStore();
    },
  },
  template: `
    <aside class="card sticky top-24">
      <nav class="space-y-1">
        <RouterLink to="/dashboard" class="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <span class="material-symbols-rounded">home</span>
          <span>Dashboard</span>
        </RouterLink>
        <RouterLink to="/organizations" class="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition">
          <span class="material-symbols-rounded">corporate_fare</span>
          <span>Organizations</span>
        </RouterLink>
      </nav>
    </aside>
  `,
  components: { RouterLink },
};

/* -----------------------------------------------------------------------------
   Views
 ----------------------------------------------------------------------------- */

const LoginView = {
  name: "LoginView",
  data: () => ({
    adminSecret: "",
    loading: false,
    error: "",
  }),
  methods: {
    async onSubmit() {
      this.error = "";
      this.loading = true;
      const auth = useAuthStore();
      try {
        // Temporarily set the secret to test it
        const tempSecret = this.adminSecret;
        auth.setAdminSecret(tempSecret);

        // Validate by making a test API call
        await api.get("/organizations");

        // If we get here, the secret is valid
        const redirect = this.$route.query.redirect || "/dashboard";
        this.$router.replace(redirect);
      } catch (e) {
        // Clear the invalid secret
        auth.setAdminSecret("");
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Invalid admin secret";
      } finally {
        this.loading = false;
      }
    },
  },
  template: `
    <div class="min-h-[70vh] grid place-items-center">
      <div class="card w-full max-w-md">
        <div class="card-header">
          <div>
            <h1 class="card-title">Admin Access</h1>
            <p class="card-subtitle">Enter the admin secret code to continue</p>
          </div>
        </div>
        <form @submit.prevent="onSubmit" class="space-y-4">
          <div>
            <label class="label" for="secret">Admin Secret Code</label>
            <input id="secret" v-model="adminSecret" type="password" required class="input" placeholder="Enter admin secret" />
          </div>
          <div v-if="error" class="text-sm text-red-600">{{ error }}</div>
          <div class="flex items-center justify-between">
            <button class="btn" type="button" @click="$router.replace('/')" :disabled="loading">Cancel</button>
            <button class="btn-primary" type="submit" :disabled="loading">
              <span class="material-symbols-rounded">key</span>
              <span v-if="!loading">Continue</span>
              <span v-else>Checking…</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
};

const DashboardView = {
  name: "DashboardView",
  template: `
    <div class="page-wrap mt-6">
      <div class="page-aside">
        <AppSidebar />
      </div>
      <main class="page-main">
        <div class="card">
          <div class="card-header">
            <div>
              <h2 class="card-title">Admin Dashboard</h2>
              <p class="card-subtitle">Global administration interface for Access System</p>
            </div>
          </div>
          <div class="prose dark:prose-invert max-w-none">
            <p>Welcome to the Access System admin panel. Use the navigation to manage organizations and their members.</p>
            <div class="grid sm:grid-cols-2 gap-4 mt-6 not-prose">
              <RouterLink to="/organizations" class="card hover:shadow-lg transition group">
                <div class="flex items-center gap-4">
                  <div class="h-12 w-12 rounded-lg bg-brand-500 flex items-center justify-center group-hover:scale-110 transition">
                    <span class="material-symbols-rounded text-white text-2xl">corporate_fare</span>
                  </div>
                  <div>
                    <div class="font-semibold text-lg">Organizations</div>
                    <div class="text-sm text-gray-500">Manage companies and domains</div>
                  </div>
                </div>
              </RouterLink>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
  components: { AppSidebar, RouterLink },
};

const OrganizationsView = {
  name: "OrganizationsView",
  data: () => ({
    // Fetch (single)
    orgId: "",
    organization: null,
    loading: false,
    error: "",
    success: "",
    submitting: false,
    showJson: false,
    showCreateModal: false,
    domainToAdd: "",

    // Listing (admin)
    listLoading: false,
    search: "",
    page: 1,
    limit: 20,
    organizations: [],
    orgPagination: null,

    // Create/Update form
    form: {
      id: "",
      name: "",
      description: "",
      allowed_domains: [],
      allowed_domains_csv: "",
      apps_and_tools: {
        leadlogic: false,
        signals: false,
        skutrition: false,
        internal_tools: false,
      },
    },
  }),
  computed: {
    auth() {
      return useAuthStore();
    },
    isAdmin() {
      return this.auth.isAdmin;
    },
  },
  methods: {
    resetFormFromOrg(org) {
      this.form.id = org?.id || "";
      this.form.name = org?.name || "";
      this.form.description = org?.description || "";
      this.form.allowed_domains = Array.isArray(org?.allowed_domains)
        ? [...org.allowed_domains]
        : [];
      this.form.allowed_domains_csv = (org?.allowed_domains || []).join(", ");
      this.form.apps_and_tools = Object.assign(
        {
          leadlogic: false,
          signals: false,
          skutrition: false,
          internal_tools: false,
        },
        org?.apps_and_tools || {},
      );
    },
    parseDomains(csv) {
      return (csv || "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    },
    async fetchOrg() {
      this.error = "";
      this.success = "";
      this.organization = null;
      const id = (this.orgId || "").trim();
      if (!id) return;
      this.loading = true;
      try {
        const resp = await api.get(`/organizations/${encodeURIComponent(id)}`);
        this.organization = resp.data.organization;
        this.resetFormFromOrg(this.organization);
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to fetch organization";
      } finally {
        this.loading = false;
      }
    },
    async listOrgs(goToPage) {
      if (!this.isAdmin) return;
      if (typeof goToPage === "number" && goToPage >= 1) {
        this.page = goToPage;
      }
      this.error = "";
      this.listLoading = true;
      try {
        const resp = await api.get("/organizations", {
          params: {
            search: this.search || undefined,
            page: this.page,
            limit: this.limit,
          },
        });
        this.organizations = resp.data.organizations || [];
        this.orgPagination = resp.data.pagination || null;
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to list organizations";
      } finally {
        this.listLoading = false;
      }
    },
    selectOrg(o) {
      if (o?.id) {
        router.push(`/organizations/${o.id}`);
      }
    },
    closeModal() {
      this.showCreateModal = false;
      this.organization = null;
      this.error = "";
      this.success = "";
    },
    toggleAllApps(state) {
      const val = !!state;
      this.form.apps_and_tools.leadlogic = val;
      this.form.apps_and_tools.signals = val;
      this.form.apps_and_tools.skutrition = val;
      this.form.apps_and_tools.internal_tools = val;
    },
    addDomain() {
      const d = (this.domainToAdd || "").trim().toLowerCase();
      if (!d) return;
      if (!this.form.allowed_domains.includes(d)) {
        this.form.allowed_domains.push(d);
      }
      this.domainToAdd = "";
    },
    removeDomain(d) {
      this.form.allowed_domains = (this.form.allowed_domains || []).filter(
        (x) => x !== d,
      );
    },
    openCreate() {
      this.resetFormFromOrg(null);
      this.form.id = "";
      this.form.name = "";
      this.form.description = "";
      this.form.allowed_domains = [];
      this.form.allowed_domains_csv = "";
      this.form.apps_and_tools = {
        leadlogic: false,
        signals: false,
        skutrition: false,
        internal_tools: false,
      };
      this.success = "";
      this.error = "";
      this.showCreateModal = true;
    },
    async createOrg() {
      if (!this.isAdmin) return;
      this.error = "";
      this.success = "";
      this.submitting = true;
      try {
        const body = {
          name: this.form.name,
          description: this.form.description,
          allowed_domains:
            this.form.allowed_domains && this.form.allowed_domains.length
              ? this.form.allowed_domains
              : this.parseDomains(this.form.allowed_domains_csv),
          apps_and_tools: this.form.apps_and_tools,
        };
        const resp = await api.post("/organizations", body);
        this.success = `Organization created: ${resp.data.organization_id}`;
        await this.listOrgs();
        setTimeout(() => this.closeModal(), 1500);
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Create failed";
      } finally {
        this.submitting = false;
      }
    },
    async updateOrg() {
      if (!this.isAdmin || !this.form.id) return;
      this.error = "";
      this.success = "";
      this.submitting = true;
      try {
        const body = {
          name: this.form.name,
          description: this.form.description,
          allowed_domains:
            this.form.allowed_domains && this.form.allowed_domains.length
              ? this.form.allowed_domains
              : this.parseDomains(this.form.allowed_domains_csv),
          apps_and_tools: this.form.apps_and_tools,
        };
        await api.put(
          `/organizations/${encodeURIComponent(this.form.id)}`,
          body,
        );
        this.success = "Organization updated successfully";
        await this.listOrgs();
        setTimeout(() => this.closeModal(), 1500);
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Update failed";
      } finally {
        this.submitting = false;
      }
    },
    async deleteOrg() {
      if (!this.isAdmin || !this.form.id) return;
      if (!confirm("Delete this organization? This cannot be undone.")) return;
      this.error = "";
      this.success = "";
      this.submitting = true;
      try {
        await api.delete(`/organizations/${encodeURIComponent(this.form.id)}`);
        this.success = "Organization deleted";
        await this.listOrgs();
        setTimeout(() => this.closeModal(), 1500);
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Delete failed";
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    // Initial load
    this.listOrgs(1);
    // Fallback: trigger again shortly to ensure auth/token is ready
    setTimeout(() => {
      this.listOrgs(1);
    }, 50);
  },
  template: `
    <div class="page-wrap mt-6">
      <div class="page-aside">
        <AppSidebar />
      </div>
      <main class="page-main">
        <div class="card">
          <div class="card-header">
            <div>
              <h2 class="card-title">Organizations</h2>
              <p class="card-subtitle">Manage organizations, domains, and app access</p>
            </div>
            <button class="btn-primary" type="button" @click="openCreate">
              <span class="material-symbols-rounded">add</span>
              New Organization
            </button>
          </div>

          <!-- Search -->
          <div class="flex gap-2 mb-4">
            <input v-model="search" class="input flex-1" placeholder="Search by name, description, or domain">
            <button class="btn" @click="listOrgs(1)" :disabled="listLoading">
              <span class="material-symbols-rounded">search</span>
              Search
            </button>
          </div>

          <!-- Loading/Error -->
          <div v-if="listLoading" class="text-center py-8 text-gray-500">Loading organizations...</div>
          <div v-if="error" class="text-sm text-red-600 mb-4">{{ error }}</div>
          <div v-if="success" class="text-sm text-green-600 mb-4">{{ success }}</div>

          <!-- Organizations Table -->
          <div v-if="!listLoading && organizations.length" class="overflow-x-auto">
            <table class="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Domains</th>
                  <th>Members</th>
                  <th>Apps Enabled</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in organizations" :key="o.id" class="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer" @click="selectOrg(o)">
                  <td class="font-medium">{{ o.name }}</td>
                  <td>
                    <div class="flex flex-wrap gap-1">
                      <span v-for="d in (o.allowed_domains || []).slice(0, 2)" :key="d" class="badge badge-muted text-xs">{{ d }}</span>
                      <span v-if="(o.allowed_domains || []).length > 2" class="badge badge-muted text-xs">+{{ (o.allowed_domains || []).length - 2 }}</span>
                    </div>
                  </td>
                  <td>{{ o.member_count || 0 }}</td>
                  <td>
                    <div class="flex gap-1">
                      <span v-if="o.apps_and_tools?.leadlogic" class="badge badge-brand text-xs">LL</span>
                      <span v-if="o.apps_and_tools?.signals" class="badge badge-brand text-xs">SG</span>
                      <span v-if="o.apps_and_tools?.skutrition" class="badge badge-brand text-xs">SK</span>
                      <span v-if="o.apps_and_tools?.internal_tools" class="badge badge-brand text-xs">IT</span>
                    </div>
                  </td>
                  <td class="text-right">
                    <button class="btn btn-sm" @click.stop="selectOrg(o)">
                      <span class="material-symbols-rounded text-sm">edit</span>
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="!listLoading && !organizations.length" class="text-center py-12">
            <span class="material-symbols-rounded text-6xl text-gray-300 mb-4">corporate_fare</span>
            <p class="text-gray-500 mb-4">No organizations found</p>
            <button class="btn" type="button" @click="listOrgs(1)">
              <span class="material-symbols-rounded">refresh</span>
              Refresh
            </button>
          </div>

          <!-- Pagination -->
          <div v-if="orgPagination && orgPagination.pages > 1" class="flex items-center justify-between mt-6 pt-4 border-t">
            <div class="text-sm text-gray-500">
              Page {{ orgPagination.page }} of {{ orgPagination.pages }} — {{ orgPagination.total }} total
            </div>
            <div class="flex gap-2">
              <button class="btn" :disabled="page<=1 || listLoading" @click="listOrgs(page-1)">
                <span class="material-symbols-rounded">chevron_left</span>
                Previous
              </button>
              <button class="btn" :disabled="page>=orgPagination.pages || listLoading" @click="listOrgs(page+1)">
                Next
                <span class="material-symbols-rounded">chevron_right</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal -->
        <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" @click.self="closeModal">
          <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div class="sticky top-0 bg-white dark:bg-gray-900 border-b px-6 py-4 flex items-center justify-between">
              <h3 class="text-xl font-bold">{{ form.id ? 'Edit Organization' : 'Create Organization' }}</h3>
              <button class="btn" @click="closeModal">
                <span class="material-symbols-rounded">close</span>
              </button>
            </div>

            <div class="p-6 space-y-4">
              <div v-if="error" class="text-sm text-red-600 p-3 bg-red-50 dark:bg-red-900/20 rounded">{{ error }}</div>
              <div v-if="success" class="text-sm text-green-600 p-3 bg-green-50 dark:bg-green-900/20 rounded">{{ success }}</div>

              <div>
                <label class="label">Name *</label>
                <input v-model="form.name" class="input" placeholder="Company Inc." required />
              </div>

              <div>
                <label class="label">Description</label>
                <input v-model="form.description" class="input" placeholder="Optional description" />
              </div>

              <div>
                <label class="label">Allowed Domains</label>
                <div class="flex flex-wrap gap-2 mb-2">
                  <span v-for="d in form.allowed_domains" :key="d" class="badge badge-muted">
                    {{ d }}
                    <button type="button" class="ml-2 material-symbols-rounded text-xs hover:text-red-600" @click="removeDomain(d)">close</button>
                  </span>
                </div>
                <div class="flex gap-2">
                  <input v-model="domainToAdd" class="input flex-1" placeholder="example.com" @keyup.enter="addDomain" />
                  <button class="btn" type="button" @click="addDomain">
                    <span class="material-symbols-rounded">add</span>
                    Add
                  </button>
                </div>
                <p class="text-xs text-gray-500 mt-2">Or paste comma-separated:</p>
                <input v-model="form.allowed_domains_csv" class="input mt-1" placeholder="acme.com, acmecorp.com" />
              </div>

              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="label mb-0">Apps & Tools</label>
                  <div class="flex gap-2">
                    <button class="btn btn-sm" type="button" @click="toggleAllApps(true)">Enable All</button>
                    <button class="btn btn-sm" type="button" @click="toggleAllApps(false)">Disable All</button>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.leadlogic" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Leadlogic</span>
                  </label>
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.signals" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Signals</span>
                  </label>
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.skutrition" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Skutrition</span>
                  </label>
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.internal_tools" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Internal Tools</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="sticky bottom-0 bg-white dark:bg-gray-900 border-t px-6 py-4 flex items-center justify-between">
              <button v-if="form.id" class="btn text-red-600" type="button" @click="deleteOrg" :disabled="submitting">
                <span class="material-symbols-rounded">delete</span>
                Delete
              </button>
              <div v-else></div>
              <div class="flex gap-2">
                <button class="btn" type="button" @click="closeModal" :disabled="submitting">Cancel</button>
                <button v-if="form.id" class="btn-primary" type="button" @click="updateOrg" :disabled="submitting">
                  <span class="material-symbols-rounded">save</span>
                  {{ submitting ? 'Saving...' : 'Save Changes' }}
                </button>
                <button v-else class="btn-primary" type="button" @click="createOrg" :disabled="submitting">
                  <span class="material-symbols-rounded">add</span>
                  {{ submitting ? 'Creating...' : 'Create' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
  components: { AppSidebar },
};

const OrganizationDetailView = {
  name: "OrganizationDetailView",
  data: () => ({
    orgId: "",
    organization: null,
    loading: false,
    error: "",
    success: "",
    submitting: false,
    // Editing
    isEditing: false,
    domainToAdd: "",
    form: {
      name: "",
      description: "",
      allowed_domains: [],
      allowed_domains_csv: "",
      apps_and_tools: {
        leadlogic: false,
        signals: false,
        skutrition: false,
        internal_tools: false,
      },
    },
    // Members
    members: [],
    membersLoading: false,
    membersError: "",
    membersPage: 1,
    membersLimit: 25,
    membersPagination: null,
    selectedMembers: [],
    inviteEmail: "",
  }),
  computed: {
    auth() {
      return useAuthStore();
    },
    isAdmin() {
      return this.auth.isAdmin;
    },
  },
  methods: {
    resetFormFromOrg(org) {
      this.form.name = org?.name || "";
      this.form.description = org?.description || "";
      this.form.allowed_domains = Array.isArray(org?.allowed_domains)
        ? [...org.allowed_domains]
        : [];
      this.form.allowed_domains_csv = (org?.allowed_domains || []).join(", ");
      this.form.apps_and_tools = Object.assign(
        {
          leadlogic: false,
          signals: false,
          skutrition: false,
          internal_tools: false,
        },
        org?.apps_and_tools || {},
      );
    },
    parseDomains(csv) {
      return (csv || "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
    },
    async fetchOrg() {
      if (!this.orgId) return;
      this.loading = true;
      this.error = "";
      try {
        const resp = await api.get(
          `/organizations/${encodeURIComponent(this.orgId)}`,
        );
        this.organization = resp.data.organization;
        this.resetFormFromOrg(this.organization);
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to load organization";
      } finally {
        this.loading = false;
      }
    },
    async fetchMembers() {
      if (!this.orgId) return;
      this.membersLoading = true;
      this.membersError = "";
      try {
        const resp = await api.get(
          `/organizations/${encodeURIComponent(this.orgId)}/members`,
          {
            params: {
              page: this.membersPage,
              limit: this.membersLimit,
            },
          },
        );
        this.members = resp.data.members || [];
        this.membersPagination = resp.data.pagination || null;
      } catch (e) {
        this.membersError =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to load members";
      } finally {
        this.membersLoading = false;
      }
    },
    startEditing() {
      this.isEditing = true;
      this.resetFormFromOrg(this.organization);
    },
    cancelEditing() {
      this.isEditing = false;
      this.resetFormFromOrg(this.organization);
      this.error = "";
    },
    toggleAllApps(state) {
      const val = !!state;
      this.form.apps_and_tools.leadlogic = val;
      this.form.apps_and_tools.signals = val;
      this.form.apps_and_tools.skutrition = val;
      this.form.apps_and_tools.internal_tools = val;
    },
    addDomain() {
      const d = (this.domainToAdd || "").trim().toLowerCase();
      if (!d) return;
      if (!this.form.allowed_domains.includes(d)) {
        this.form.allowed_domains.push(d);
      }
      this.domainToAdd = "";
    },
    removeDomain(d) {
      this.form.allowed_domains = (this.form.allowed_domains || []).filter(
        (x) => x !== d,
      );
    },
    async saveOrg() {
      if (!this.isAdmin || !this.orgId) return;
      this.error = "";
      this.success = "";
      this.submitting = true;
      try {
        const body = {
          name: this.form.name,
          description: this.form.description,
          allowed_domains:
            this.form.allowed_domains && this.form.allowed_domains.length
              ? this.form.allowed_domains
              : this.parseDomains(this.form.allowed_domains_csv),
          apps_and_tools: this.form.apps_and_tools,
        };
        await api.put(`/organizations/${encodeURIComponent(this.orgId)}`, body);
        this.success = "Organization updated successfully";
        await this.fetchOrg();
        this.isEditing = false;
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Update failed";
      } finally {
        this.submitting = false;
      }
    },
    async deleteOrg() {
      if (!this.isAdmin || !this.orgId) return;
      if (!confirm("Delete this organization? This cannot be undone.")) return;
      this.submitting = true;
      try {
        await api.delete(`/organizations/${encodeURIComponent(this.orgId)}`);
        this.$router.push("/organizations");
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Delete failed";
        this.submitting = false;
      }
    },
    async inviteMember() {
      const email = (this.inviteEmail || "").trim().toLowerCase();
      if (!email) return;
      this.membersError = "";
      this.success = "";
      try {
        await api.post("/auth/request-otp", {
          email,
          purpose: "email_confirmation",
        });
        this.success = `Invite sent to ${email}`;
        this.inviteEmail = "";
      } catch (e) {
        this.membersError =
          e?.response?.data?.error?.message || e?.message || "Invite failed";
      }
    },
    async updateMemberStatus(member, status) {
      try {
        await api.patch(`/members/${encodeURIComponent(member.id)}/status`, {
          status,
        });
        this.success = `Member status updated to ${status}`;
        await this.fetchMembers();
      } catch (e) {
        this.membersError =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to update member";
      }
    },
    async resetMemberPassword(member) {
      try {
        await api.post(
          `/members/${encodeURIComponent(member.id)}/reset-password`,
        );
        this.success = "Password reset email sent";
      } catch (e) {
        this.membersError =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to reset password";
      }
    },
    async deleteMember(member) {
      if (!confirm(`Delete member ${member.email}?`)) return;
      try {
        await api.delete(`/members/${encodeURIComponent(member.id)}`);
        this.success = "Member deleted";
        await this.fetchMembers();
      } catch (e) {
        this.membersError =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to delete member";
      }
    },
  },
  mounted() {
    this.orgId = this.$route.params.id;
    this.fetchOrg();
    this.fetchMembers();
  },
  template: `
    <div class="page-wrap mt-6">
      <div class="page-aside">
        <AppSidebar />
      </div>
      <main class="page-main">
        <!-- Breadcrumb -->
        <div class="mb-4 flex items-center gap-2 text-sm text-gray-500">
          <RouterLink to="/organizations" class="hover:text-brand-500">Organizations</RouterLink>
          <span class="material-symbols-rounded text-xs">chevron_right</span>
          <span class="text-gray-900 dark:text-gray-100">{{ organization?.name || 'Loading...' }}</span>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="card">
          <div class="text-center py-12 text-gray-500">Loading organization...</div>
        </div>

        <!-- Error -->
        <div v-if="error && !loading" class="card">
          <div class="text-red-600">{{ error }}</div>
        </div>

        <!-- Organization Details -->
        <div v-if="organization && !loading" class="space-y-6">
          <!-- Header Card -->
          <div class="card">
            <div class="card-header">
              <div>
                <h1 class="card-title">{{ organization.name }}</h1>
                <p class="card-subtitle">{{ organization.description || 'No description' }}</p>
              </div>
              <div class="flex gap-2">
                <button v-if="!isEditing" class="btn" @click="startEditing">
                  <span class="material-symbols-rounded">edit</span>
                  Edit
                </button>
                <button v-if="!isEditing" class="btn text-red-600" @click="deleteOrg" :disabled="submitting">
                  <span class="material-symbols-rounded">delete</span>
                  Delete
                </button>
              </div>
            </div>

            <div v-if="success" class="text-sm text-green-600 p-3 bg-green-50 dark:bg-green-900/20 rounded mb-4">{{ success }}</div>

            <!-- View Mode -->
            <div v-if="!isEditing" class="grid md:grid-cols-2 gap-6">
              <div>
                <h3 class="font-semibold mb-3">Allowed Domains</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="d in organization.allowed_domains" :key="d" class="badge badge-muted">{{ d }}</span>
                  <span v-if="!organization.allowed_domains?.length" class="text-sm text-gray-500">No domains</span>
                </div>
              </div>
              <div>
                <h3 class="font-semibold mb-3">Apps & Tools</h3>
                <div class="grid grid-cols-2 gap-2">
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-rounded text-sm" :class="organization.apps_and_tools?.leadlogic ? 'text-green-600' : 'text-gray-300'">
                      {{ organization.apps_and_tools?.leadlogic ? 'check_circle' : 'cancel' }}
                    </span>
                    <span :class="organization.apps_and_tools?.leadlogic ? '' : 'text-gray-400'">Leadlogic</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-rounded text-sm" :class="organization.apps_and_tools?.signals ? 'text-green-600' : 'text-gray-300'">
                      {{ organization.apps_and_tools?.signals ? 'check_circle' : 'cancel' }}
                    </span>
                    <span :class="organization.apps_and_tools?.signals ? '' : 'text-gray-400'">Signals</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-rounded text-sm" :class="organization.apps_and_tools?.skutrition ? 'text-green-600' : 'text-gray-300'">
                      {{ organization.apps_and_tools?.skutrition ? 'check_circle' : 'cancel' }}
                    </span>
                    <span :class="organization.apps_and_tools?.skutrition ? '' : 'text-gray-400'">Skutrition</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="material-symbols-rounded text-sm" :class="organization.apps_and_tools?.internal_tools ? 'text-green-600' : 'text-gray-300'">
                      {{ organization.apps_and_tools?.internal_tools ? 'check_circle' : 'cancel' }}
                    </span>
                    <span :class="organization.apps_and_tools?.internal_tools ? '' : 'text-gray-400'">Internal Tools</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Edit Mode -->
            <div v-if="isEditing" class="space-y-4">
              <div v-if="error" class="text-sm text-red-600 p-3 bg-red-50 dark:bg-red-900/20 rounded">{{ error }}</div>

              <div class="grid md:grid-cols-2 gap-4">
                <div>
                  <label class="label">Name *</label>
                  <input v-model="form.name" class="input" required />
                </div>
                <div>
                  <label class="label">Description</label>
                  <input v-model="form.description" class="input" />
                </div>
              </div>

              <div>
                <label class="label">Allowed Domains</label>
                <div class="flex flex-wrap gap-2 mb-2">
                  <span v-for="d in form.allowed_domains" :key="d" class="badge badge-muted">
                    {{ d }}
                    <button type="button" class="ml-2 material-symbols-rounded text-xs hover:text-red-600" @click="removeDomain(d)">close</button>
                  </span>
                </div>
                <div class="flex gap-2">
                  <input v-model="domainToAdd" class="input flex-1" placeholder="example.com" @keyup.enter="addDomain" />
                  <button class="btn" type="button" @click="addDomain">
                    <span class="material-symbols-rounded">add</span>
                    Add
                  </button>
                </div>
                <p class="text-xs text-gray-500 mt-2">Or paste comma-separated:</p>
                <input v-model="form.allowed_domains_csv" class="input mt-1" placeholder="acme.com, acmecorp.com" />
              </div>

              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="label mb-0">Apps & Tools</label>
                  <div class="flex gap-2">
                    <button class="btn btn-sm" type="button" @click="toggleAllApps(true)">Enable All</button>
                    <button class="btn btn-sm" type="button" @click="toggleAllApps(false)">Disable All</button>
                  </div>
                </div>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.leadlogic" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Leadlogic</span>
                  </label>
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.signals" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Signals</span>
                  </label>
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.skutrition" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Skutrition</span>
                  </label>
                  <label class="inline-flex items-center gap-2">
                    <input type="checkbox" v-model="form.apps_and_tools.internal_tools" class="rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                    <span>Internal Tools</span>
                  </label>
                </div>
              </div>

              <div class="flex gap-2 pt-4 border-t">
                <button class="btn" @click="cancelEditing" :disabled="submitting">Cancel</button>
                <button class="btn-primary" @click="saveOrg" :disabled="submitting">
                  <span class="material-symbols-rounded">save</span>
                  {{ submitting ? 'Saving...' : 'Save Changes' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Members Card -->
          <div class="card">
            <div class="card-header">
              <div>
                <h2 class="card-title">Members</h2>
                <p class="card-subtitle">{{ membersPagination?.total || 0 }} members in this organization</p>
              </div>
            </div>

            <!-- Invite -->
            <div class="flex gap-2 mb-4">
              <input v-model="inviteEmail" class="input flex-1" placeholder="user@domain.com" />
              <button class="btn-primary" @click="inviteMember">
                <span class="material-symbols-rounded">mail</span>
                Invite Member
              </button>
            </div>

            <div v-if="membersError" class="text-sm text-red-600 mb-4">{{ membersError }}</div>

            <!-- Members Table -->
            <div v-if="membersLoading" class="text-center py-8 text-gray-500">Loading members...</div>

            <div v-else-if="members.length" class="overflow-x-auto">
              <table class="table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Confirmed</th>
                    <th>Last Login</th>
                    <th class="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in members" :key="m.id">
                    <td>{{ m.email }}</td>
                    <td>
                      <span class="badge" :class="m.status === 'active' ? 'badge-brand' : 'badge-muted'">
                        {{ m.status }}
                      </span>
                    </td>
                    <td>{{ m.email_confirmed ? 'Yes' : 'No' }}</td>
                    <td class="text-sm text-gray-500">{{ m.last_login || '-' }}</td>
                    <td class="text-right">
                      <div class="inline-flex gap-1">
                        <button v-if="m.status !== 'active'" class="btn btn-sm" @click="updateMemberStatus(m, 'active')" title="Activate">
                          <span class="material-symbols-rounded text-sm">check_circle</span>
                        </button>
                        <button v-if="m.status === 'active'" class="btn btn-sm" @click="updateMemberStatus(m, 'inactive')" title="Deactivate">
                          <span class="material-symbols-rounded text-sm">block</span>
                        </button>
                        <button class="btn btn-sm" @click="resetMemberPassword(m)" title="Reset Password">
                          <span class="material-symbols-rounded text-sm">key</span>
                        </button>
                        <button class="btn btn-sm text-red-600" @click="deleteMember(m)" title="Delete">
                          <span class="material-symbols-rounded text-sm">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center py-12">
              <span class="material-symbols-rounded text-6xl text-gray-300 mb-4">group</span>
              <p class="text-gray-500">No members yet. Invite the first member above.</p>
            </div>

            <!-- Pagination -->
            <div v-if="membersPagination && membersPagination.pages > 1" class="flex items-center justify-between mt-4 pt-4 border-t">
              <div class="text-sm text-gray-500">
                Page {{ membersPagination.page }} of {{ membersPagination.pages }}
              </div>
              <div class="flex gap-2">
                <button class="btn" :disabled="membersPage<=1" @click="membersPage--; fetchMembers()">
                  <span class="material-symbols-rounded">chevron_left</span>
                  Previous
                </button>
                <button class="btn" :disabled="membersPage>=membersPagination.pages" @click="membersPage++; fetchMembers()">
                  Next
                  <span class="material-symbols-rounded">chevron_right</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
  components: { AppSidebar, RouterLink },
};

const MembersView = {
  name: "MembersView",
  data: () => ({
    orgId: "",
    status: "",
    page: 1,
    limit: 25,
    members: [],
    pagination: null,
    selected: [],
    selectAll: false,
    inviteEmail: "",
    loading: false,
    error: "",
    success: "",
  }),
  computed: {
    auth() {
      return useAuthStore();
    },
    isAdmin() {
      return this.auth.isAdmin;
    },
  },
  methods: {
    async listMembers() {
      this.error = "";
      this.success = "";
      this.members = [];
      const id = (this.orgId || "").trim();
      if (!id) return;
      this.loading = true;
      try {
        const resp = await api.get(
          `/organizations/${encodeURIComponent(id)}/members`,
          {
            params: {
              status: this.status || undefined,
              page: this.page,
              limit: this.limit,
            },
          },
        );
        this.members = resp.data.members || [];
        this.pagination = resp.data.pagination || null;
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to list members";
      } finally {
        this.loading = false;
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selected = (this.members || []).map((m) => m.id);
      } else {
        this.selected = [];
      }
    },
    async bulkUpdateStatus(status) {
      if (!this.selected.length) return;
      this.error = "";
      this.success = "";
      try {
        for (const id of this.selected) {
          await api.patch(`/members/${encodeURIComponent(id)}/status`, {
            status,
          });
        }
        this.success = `Updated ${this.selected.length} member(s) to ${status}`;
        await this.listMembers();
        this.selected = [];
        this.selectAll = false;
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Bulk update failed";
      }
    },
    async bulkDelete() {
      if (!this.selected.length) return;
      if (!confirm(`Delete ${this.selected.length} member(s)?`)) return;
      this.error = "";
      this.success = "";
      try {
        for (const id of this.selected) {
          await api.delete(`/members/${encodeURIComponent(id)}`);
        }
        this.success = `Deleted ${this.selected.length} member(s)`;
        await this.listMembers();
        this.selected = [];
        this.selectAll = false;
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Bulk delete failed";
      }
    },
    async bulkReset() {
      if (!this.selected.length) return;
      this.error = "";
      this.success = "";
      try {
        for (const id of this.selected) {
          await api.post(`/members/${encodeURIComponent(id)}/reset-password`);
        }
        this.success = `Sent reset OTP to ${this.selected.length} member(s)`;
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Bulk reset failed";
      }
    },
    async invite() {
      const email = (this.inviteEmail || "").trim().toLowerCase();
      if (!email) return;
      this.error = "";
      this.success = "";
      try {
        await api.post("/auth/request-otp", {
          email,
          purpose: "email_confirmation",
        });
        this.success = `Invite sent to ${email}`;
        this.inviteEmail = "";
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Invite failed";
      }
    },
    async updateStatus(m, status) {
      try {
        await api.patch(`/members/${encodeURIComponent(m.id)}/status`, {
          status,
        });
        this.success = `Status updated to ${status}`;
        await this.listMembers();
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to update status";
      }
    },
    async resetPassword(m) {
      try {
        await api.post(`/members/${encodeURIComponent(m.id)}/reset-password`);
        this.success = "Password reset OTP sent to member";
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message ||
          e?.message ||
          "Failed to trigger reset";
      }
    },
    async deleteMember(m) {
      if (!confirm(`Delete member ${m.email}?`)) return;
      try {
        await api.delete(`/members/${encodeURIComponent(m.id)}`);
        this.success = "Member deleted";
        await this.listMembers();
      } catch (e) {
        this.error =
          e?.response?.data?.error?.message || e?.message || "Failed to delete";
      }
    },
  },
  template: `
    <div class="page-wrap mt-6">
      <div class="page-aside">
        <AppSidebar />
      </div>
      <main class="page-main">
        <div class="card">
          <div class="card-header">
            <div>
              <h2 class="card-title">Members</h2>
              <p class="card-subtitle">List and manage members in an organization</p>
            </div>
          </div>
          <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <div>
              <label class="label">Organization ID</label>
              <input v-model="orgId" class="input" placeholder="Enter organization id" />
            </div>
            <div>
              <label class="label">Status</label>
              <select v-model="status" class="input">
                <option value="">Any</option>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="pending">pending</option>
              </select>
            </div>
            <div class="flex items-end">
              <button class="btn w-full" @click="listMembers" :disabled="loading">
                <span class="material-symbols-rounded">search</span>
                Search
              </button>
            </div>
          </div>
          <div class="flex flex-wrap gap-2 mb-3">
            <button class="btn" @click="bulkUpdateStatus('active')" :disabled="!selected.length">Bulk Activate</button>
            <button class="btn" @click="bulkUpdateStatus('inactive')" :disabled="!selected.length">Bulk Deactivate</button>
            <button class="btn" @click="bulkReset" :disabled="!selected.length">Bulk Reset Password</button>
            <button class="btn" @click="bulkDelete" :disabled="!selected.length">Bulk Delete</button>
          </div>
          <div class="flex gap-2 items-end mb-4">
            <div class="flex-1">
              <label class="label">Invite email</label>
              <input v-model="inviteEmail" class="input" placeholder="user@domain.com" />
            </div>
            <button class="btn" @click="invite">
              <span class="material-symbols-rounded">mail</span>
              Send Invite
            </button>
          </div>
          <div class="flex items-center gap-2 mb-2">
            <label class="label mb-0">Per page</label>
            <select v-model.number="limit" class="input w-28" @change="listMembers">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
            </div>
          <div v-if="loading">Loading…</div>
          <div v-if="error" class="text-sm text-red-600">{{ error }}</div>
          <div v-if="success" class="text-sm text-green-600">{{ success }}</div>
          <div v-if="members.length" class="overflow-x-auto">
            <table class="table">
              <thead>
                <tr>
                  <th><input type="checkbox" v-model="selectAll" @change="toggleSelectAll" /></th>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Confirmed</th>
                  <th>Last Login</th>
                  <th class="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in members" :key="m.id">
                  <td><input type="checkbox" :value="m.id" v-model="selected" /></td>
                  <td class="font-mono text-xs">{{ m.id }}</td>
                  <td>{{ m.email }}</td>
                  <td>
                    <span class="badge" :class="m.status === 'active' ? 'badge-brand' : 'badge-muted'">
                      {{ m.status }}
                    </span>
                  </td>
                  <td>{{ m.email_confirmed ? 'yes' : 'no' }}</td>
                  <td>{{ m.last_login || '-' }}</td>
                  <td class="text-right">
                    <div class="inline-flex gap-2">
                      <button class="btn" @click="updateStatus(m, 'active')" :disabled="m.status==='active'">
                        <span class="material-symbols-rounded">task_alt</span>
                        Activate
                      </button>
                      <button class="btn" @click="updateStatus(m, 'inactive')" :disabled="m.status==='inactive'">
                        <span class="material-symbols-rounded">do_not_disturb_on</span>
                        Deactivate
                      </button>
                      <button class="btn" @click="resetPassword(m)">
                        <span class="material-symbols-rounded">key</span>
                        Reset
                      </button>
                      <button class="btn" @click="deleteMember(m)">
                        <span class="material-symbols-rounded">delete</span>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else-if="!loading && !error" class="text-sm text-gray-500">No results</div>
          <div v-if="pagination" class="flex items-center justify-between mt-3">
            <div class="text-xs text-gray-500">
              Page {{ pagination.page }} of {{ pagination.pages }} — Total {{ pagination.total }}
            </div>
            <div class="flex gap-2">
              <button class="btn" :disabled="page<=1 || loading" @click="page=page-1; listMembers()">
                <span class="material-symbols-rounded">chevron_left</span>
                Prev
              </button>
              <button class="btn" :disabled="pagination && page>=pagination.pages || loading" @click="page=page+1; listMembers()">
                Next
                <span class="material-symbols-rounded">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  `,
  components: { AppSidebar },
};

const MeView = {
  name: "MeView",
  data: () => ({
    profile: null,
    loading: false,
    error: "",
  }),
  async mounted() {
    this.loading = true;
    try {
      const resp = await api.get("/members/me");
      this.profile = resp.data.member;
    } catch (e) {
      this.error =
        e?.response?.data?.error?.message ||
        e?.message ||
        "Failed to load profile";
    } finally {
      this.loading = false;
    }
  },
  template: `
    <div class="page-wrap mt-6">
      <div class="page-aside">
        <AppSidebar />
      </div>
      <main class="page-main">
        <div class="card">
          <div class="card-header">
            <div>
              <h2 class="card-title">My Profile</h2>
              <p class="card-subtitle">Your account details</p>
            </div>
          </div>
          <div v-if="loading">Loading…</div>
          <div v-if="error" class="text-sm text-red-600">{{ error }}</div>
          <pre v-if="profile" class="mt-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg p-4 overflow-auto text-xs">{{ profile }}</pre>
        </div>
      </main>
    </div>
  `,
  components: { AppSidebar },
};

/* -----------------------------------------------------------------------------
   Router
 ----------------------------------------------------------------------------- */
router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/dashboard" },
    { path: "/login", component: LoginView, meta: { public: true } },
    {
      path: "/dashboard",
      component: DashboardView,
      meta: { requiresAuth: true },
    },
    {
      path: "/organizations/:id",
      component: OrganizationDetailView,
      meta: { requiresAuth: true },
    },
    {
      path: "/organizations",
      component: OrganizationsView,
      meta: { requiresAuth: true },
    },
    { path: "/:pathMatch(.*)*", redirect: "/dashboard" },
  ],
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta?.requiresAuth && !auth.adminSecret) {
    return { path: "/login", query: { redirect: to.fullPath } };
  }
  if (to.path === "/login" && auth.adminSecret) {
    return { path: "/dashboard" };
  }
  return true;
});

/* -----------------------------------------------------------------------------
   App Root
 ----------------------------------------------------------------------------- */
const AppShell = {
  name: "AppShell",
  components: { AppHeader, RouterView },
  template: `
    <div>
      <AppHeader />
      <div class="container py-6">
        <RouterView />
      </div>
      <footer class="container py-10 text-xs text-gray-500">
        <div class="border-t border-gray-200 dark:border-gray-800 pt-6">
          <div class="flex items-center justify-between">
            <div>© {{ new Date().getFullYear() }} Access System</div>
            <div class="flex items-center gap-4">
              <a href="https://example.com" target="_blank" rel="noreferrer" class="hover:underline">Docs</a>
              <a href="#" class="hover:underline">Status</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  `,
};

/* -----------------------------------------------------------------------------
   Bootstrap
 ----------------------------------------------------------------------------- */
const app = createApp(App);

app.config.globalProperties.$api = api;

const pinia = createPinia();
app.use(pinia);
app.use(router);

app.component("AppHeader", AppHeader);
app.component("AppSidebar", AppSidebar);

app.mount("#app");
